import axios from "axios";

// Criação da instância do axios
//
// Em desenvolvimento:
// - Defina REACT_APP_API_URL=http://localhost:3001/api no arquivo .env do frontend
// - Assim, as requisições vão para o backend local
//
// Em produção (Render.com, backend servindo o frontend):
// - NÃO defina REACT_APP_API_URL
// - O axios usará a baseURL relativa /api, funcionando normalmente
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor para adicionar token JWT nas requisições
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Interceptor para tratamento de respostas
api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (error.response) {
      // Erro de resposta do servidor
      const errorMessage = error.response.data?.error || "Erro na requisição";

      // Se for erro 401 (não autorizado), limpar token e redirecionar
      // Mas não durante o processo de login (/auth/login)
      if (error.response.status === 401) {
        const isLoginRequest = error.config?.url?.includes("/auth/login");

        if (!isLoginRequest) {
          localStorage.removeItem("authToken");
          window.location.href = "/login";
        }
      }

      throw new Error(errorMessage);
    } else if (error.request) {
      // Erro de rede
      throw new Error("Erro de conexão com o servidor");
    } else {
      // Outro tipo de erro
      throw new Error("Erro inesperado");
    }
  },
);

// Serviços de autenticação
export const authService = {
  login: async (credentials) => {
    // A baseURL já é /api, e a rota registrada no servidor é /auth/login
    return api.post("/auth/login", {
      email: credentials.email,
      senha: credentials.senha, // Corrigido de password para senha
    });
  },

  verify: async () => {
    // A rota correta é /auth/verify
    return api.get("/auth/verify");
  },
  forgotPassword: async (email) => {
    return api.post("/auth/forgot-password", { email });
  },
  resetPassword: async ({ token, novaSenha, confirmarSenha }) => {
    return api.post("/auth/reset-password", {
      token,
      novaSenha,
      confirmarSenha,
    });
  },
};

// Serviços da agenda
export const agendaService = {
  getAgenda: async (params = {}) => {
    return api.get("/agenda", { params });
  },
};

// Serviços de usuários
export const usuarioService = {
  getAll: async (params = {}) => {
    return api.get("/usuarios", { params });
  },

  getById: async (id) => {
    return api.get(`/usuarios/${id}`);
  },

  create: async (userData) => {
    return api.post("/usuarios", userData);
  },

  invite: async (userData) => {
    return api.post("/usuarios/invite", userData);
  },

  update: async (id, userData) => {
    return api.put(`/usuarios/${id}`, userData);
  },

  delete: async (id) => {
    return api.delete(`/usuarios/${id}`);
  },

  alterarSenha: async (senhaData) => {
    return api.put("/usuarios/alterar-senha", senhaData);
  },
};

// Serviços de clientes
export const clienteService = {
  getAll: async (params = {}) => {
    return api.get("/clientes", { params });
  },

  getById: async (id) => {
    return api.get(`/clientes/${id}`);
  },

  create: async (clienteData) => {
    return api.post("/clientes", clienteData);
  },

  update: async (id, clienteData) => {
    return api.put(`/clientes/${id}`, clienteData);
  },

  delete: async (id) => {
    return api.delete(`/clientes/${id}`);
  },

  consultaCNPJ: async (cnpj) => {
    return api.get(`/clientes/consulta-cnpj`, { params: { cnpj } });
  },
};

// Serviços de obrigações
export const obrigacaoService = {
  getAll: async (params = {}) => {
    return api.get("/obrigacoes", { params });
  },

  getById: async (id) => {
    return api.get(`/obrigacoes/${id}`);
  },

  create: async (obrigacaoData) => {
    return api.post("/obrigacoes", obrigacaoData);
  },

  update: async (id, obrigacaoData) => {
    return api.put(`/obrigacoes/${id}`, obrigacaoData);
  },

  updateStatus: async (id, status) => {
    return api.patch(`/obrigacoes/${id}/status`, { status });
  },

  updateStatusBulk: async (ids, status) => {
    return api.patch("/obrigacoes/bulk/status", { ids, status });
  },

  delete: async (id) => {
    return api.delete(`/obrigacoes/${id}`);
  },

  // Relatório de obrigações enviadas
  getRelatorioObrigacoesEnviadas: async (params = {}) => {
    return api.get("/obrigacoes/relatorio/enviadas", { params });
  },
};

// Serviços de tipos de obrigação
export const tipoObrigacaoService = {
  getAll: async (params = {}) => {
    return api.get("/tipos-obrigacoes", { params });
  },

  getByRegimeTributario: async (regimeTributario) => {
    return api.get(`/tipos-obrigacoes/regime/${regimeTributario}`);
  },

  getById: async (id) => {
    return api.get(`/tipos-obrigacoes/${id}`);
  },

  create: async (tipoData) => {
    return api.post("/tipos-obrigacoes", tipoData);
  },

  update: async (id, tipoData) => {
    return api.put(`/tipos-obrigacoes/${id}`, tipoData);
  },

  delete: async (id) => {
    return api.delete(`/tipos-obrigacoes/${id}`);
  },

  getDefault: async () => {
    return api.get("/tipos-obrigacoes/default");
  },

  createMultiple: async (tiposData) => {
    return api.post("/tipos-obrigacoes/multiple", {
      tipos_obrigacoes: tiposData,
    });
  },
};

// Serviços de setores
export const setorService = {
  getAll: async (params = {}) => {
    return api.get("/setores", { params });
  },

  getById: async (id) => {
    return api.get(`/setores/${id}`);
  },

  create: async (setorData) => {
    return api.post("/setores", setorData);
  },

  update: async (id, setorData) => {
    return api.put(`/setores/${id}`, setorData);
  },

  delete: async (id) => {
    return api.delete(`/setores/${id}`);
  },
};

// Serviços de prioridades
export const prioridadeService = {
  getAll: async (params = {}) => {
    return api.get("/prioridades", { params });
  },

  getById: async (id) => {
    return api.get(`/prioridades/${id}`);
  },

  create: async (prioridadeData) => {
    return api.post("/prioridades", prioridadeData);
  },

  update: async (id, prioridadeData) => {
    return api.put(`/prioridades/${id}`, prioridadeData);
  },

  delete: async (id) => {
    return api.delete(`/prioridades/${id}`);
  },
};

// Serviços de recorrências
export const recorrenciaService = {
  getAll: async (params = {}) => {
    return api.get("/recorrencias", { params });
  },

  getById: async (id) => {
    return api.get(`/recorrencias/${id}`);
  },

  create: async (recorrenciaData) => {
    return api.post("/recorrencias", recorrenciaData);
  },

  createBatch: async (batchData) => {
    return api.post("/recorrencias/batch", batchData);
  },

  copyRecurrences: async (copyData) => {
    return api.post("/recorrencias/copy", copyData);
  },

  update: async (id, recorrenciaData) => {
    return api.put(`/recorrencias/${id}`, recorrenciaData);
  },

  delete: async (id) => {
    return api.delete(`/recorrencias/${id}`);
  },

  deleteWithObrigacoes: async (id) => {
    return api.delete(`/recorrencias/${id}/with-obrigacoes`);
  },
};

// Serviços de tarefas
export const tarefaService = {
  getAll: async (params = {}) => {
    return api.get("/tarefas", { params });
  },

  getById: async (id) => {
    return api.get(`/tarefas/${id}`);
  },

  create: async (tarefaData) => {
    return api.post("/tarefas", tarefaData);
  },

  update: async (id, tarefaData) => {
    return api.put(`/tarefas/${id}`, tarefaData);
  },

  updateStatus: async (id, status) => {
    return api.patch(`/tarefas/${id}/status`, { status });
  },

  updatePriorizar: async (id, priorizar) => {
    return api.patch(`/tarefas/${id}/priorizar`, { priorizar });
  },

  delete: async (id) => {
    return api.delete(`/tarefas/${id}`);
  },
};

// Novo serviço para assinaturas
export const assinaturaService = {
  getPlanos: () => api.get("/assinaturas/planos"),
  createCheckoutSession: (planoId) =>
    api.post("/assinaturas/create-checkout-session", { planoId }),
};

// Serviços de CEP
export const cepService = {
  buscarCEP: async (cep) => {
    // Remove formatação do CEP (remove hífens e espaços)
    const cepLimpo = cep.replace(/\D/g, "");

    // Valida se tem 8 dígitos
    if (cepLimpo.length !== 8) {
      throw new Error("CEP deve ter 8 dígitos");
    }

    try {
      const response = await fetch(
        `https://viacep.com.br/ws/${cepLimpo}/json/`,
      );
      const data = await response.json();

      // Verifica se o CEP foi encontrado
      if (data.erro) {
        throw new Error("CEP não encontrado");
      }

      return data;
    } catch (error) {
      if (error.message === "CEP não encontrado") {
        throw error;
      }
      throw new Error("Erro ao buscar CEP. Tente novamente.");
    }
  },
};

// Serviços administrativos (super admin)
export const adminService = {
  // ==================== ESCRITÓRIOS ====================
  getEscritorios: (params = {}) => api.get("/admin/escritorios", { params }),
  createEscritorio: (dados) => api.post("/admin/escritorios", dados),
  updateEscritorio: (id, dados) => api.put(`/admin/escritorios/${id}`, dados),
  deleteEscritorio: (id) => api.delete(`/admin/escritorios/${id}`),
  consultaCNPJ: (cnpj) =>
    api.get("/admin/escritorios/consulta-cnpj", { params: { cnpj } }),

  // ==================== DASHBOARD ====================
  getDashboardStats: () => api.get("/admin/dashboard"),

  // ==================== PLANOS ====================
  getPlanos: () => api.get("/admin/planos"),
  createPlano: (dados) => api.post("/admin/planos", dados),
  updatePlano: (id, dados) => api.put(`/admin/planos/${id}`, dados),
  deletePlano: (id) => api.delete(`/admin/planos/${id}`),

  // ==================== STRIPE ====================
  testStripe: (secretKey) =>
    api.post("/admin/stripe/test", { secret_key: secretKey }),
  getStripePrices: () => api.get("/admin/stripe/prices"),
};

// Exportar instância do axios para uso direto se necessário
export default api;
